import React from "react"
import { Box, Collapse, Icon } from "@chakra-ui/core"
import { FormProvider, useForm } from "react-hook-form"
import { NumberParam, StringParam, useQueryParam } from "use-query-params"

import Heading from "../components/typography/Heading"
import PageContainer from "../components/PageContainer"
import MaxWidthGrid from "../components/Layout/MaxWidthGrid"
import { Button } from "../components/Buttons"
import ButtonGroup from "../components/ButtonGroup"
import { ConnectedTextField as TextField } from "../components/FormFields/TextField"
import ArticleList from "../components/ArticleList"
import SEO from "../components/boilerplate/seo"

import { toSlug } from "../utils"
import { bp } from "../utils/MediaQueries"
import { TransformedBlogListQuery } from "../templates/blogList"

interface BlogListPageProps {
  data: TransformedBlogListQuery
  location: Location
}

const PAGE_SIZE = 9

const BlogListPage: React.FC<BlogListPageProps> = ({
  data: { blogs },
  location,
}) => {
  const [search, setSearch] = useQueryParam("search", StringParam)
  const [category, setCategory] = useQueryParam("category", StringParam)
  const [page = 1, setPage] = useQueryParam("page", NumberParam)
  const [showSearch, setShowSearch] = React.useState(!!search)
  const formRef = React.useRef<HTMLFormElement>()

  const methods = useForm()

  const allArticles = blogs.edges
  const categories = React.useMemo(() => {
    return Array.from(
      allArticles.reduce((acc, next) => {
        next.node.tags?.forEach((tag) => acc.add(tag))
        return acc
      }, new Set())
    )
  }, [allArticles]).map((category: string) => ({
    label: category,
    value: category,
  }))

  const allFilteredArticles = allArticles.filter((article) => {
    if (category && !article.node.sluggedTags?.includes(category)) {
      return false
    }

    if (
      showSearch &&
      search &&
      !article.node.title?.toLowerCase()?.match(search.toLowerCase())?.length
    ) {
      return false
    }

    return true
  })

  const toggleSearch = () => {
    setSearch(showSearch ? undefined : methods.getValues("search"))
    setShowSearch(!showSearch)
    if (!showSearch) methods.reset()
  }

  const handleSearchSubmit = ({ search }: { search: string }) => {
    setSearch(search)
  }

  const handleFilterChange = (value) => {
    setPage(1)
    setCategory(value ? toSlug(value) : undefined)
  }

  return (
    <PageContainer>
      <SEO
        title="Bike Advice | Best Bike Blog For Beginners | Beach Cruiser & Cycling Blogs"
        location={location}
      />
      <MaxWidthGrid>
        <Box gridColumn={bp("1/end", "2/14")}>
          <Heading size="2" fontWeight="bold" textAlign="center" mb="80">
            Bike Advice
          </Heading>
          <ButtonGroup options={categories} onChange={handleFilterChange}>
            <Button
              theme={showSearch ? "toggled" : "tertiary"}
              aria-label="Search articles"
              height="2.625rem"
              width="3.25rem"
              alignSelf="normal"
              px="1rem"
              mb="1rem"
              mr="1rem"
              onClick={toggleSearch}
            >
              <Icon name="search-2" color={showSearch ? "white" : "night"} />
            </Button>
          </ButtonGroup>
          <Collapse isOpen={showSearch}>
            <FormProvider {...methods}>
              <Box
                as="form"
                ref={formRef}
                onSubmit={methods.handleSubmit(handleSearchSubmit)}
              >
                <TextField
                  w="100%"
                  height="2.625rem"
                  name="search"
                  placeholder="Search"
                  defaultValue={search || ""}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      const event = new Event("submit", {
                        bubbles: true,
                        cancelable: true,
                      })
                      formRef?.current?.dispatchEvent(event)
                    }
                  }}
                />
                <Box
                  as="input"
                  // @ts-ignore
                  type="submit"
                  display="none"
                />
              </Box>
            </FormProvider>
          </Collapse>

          <ArticleList
            articles={allFilteredArticles.map((article) => article.node)}
            emptyText={`No articles available${search && ` for "${search}"`}`}
            pageSize={PAGE_SIZE}
            mt="5rem"
          />
        </Box>
      </MaxWidthGrid>
    </PageContainer>
  )
}

export default BlogListPage
